import ArrRight from "@/assets/icons/ic-arrow-right2.svg";
import ArrRightMobile from "@/assets/icons/ic-ar-mobile.svg";
import IconRight from "@/assets/icons/ic-arrow-right.svg";
import { AgentRecommendationBox } from "./agent-recommendation-box";
import { AgentCardItem, type AgentCard } from "./agent-card-item";
import { ShortlistData, ShortlistItem } from "@/services/project-report-service";
import { useNavigate } from "react-router-dom";

export function AgentSection({
  shortlistData,
  selectedIds,
  onToggle,
  onRequestOtherAgent,
  projectId,
}: {
  shortlistData: ShortlistData | null;
  selectedIds: string[];
  onToggle: (id: string) => void;
  onRequestOtherAgent: () => void;
  projectId: string;
}) {
  const navigate = useNavigate();
  const agentCount = shortlistData?.items.length ?? 0;
  const hasAgentCards = agentCount > 0;
  const showOtherAgentCard = agentCount > 0 && agentCount < 3;
  const showEmptyState = agentCount === 0;

  const toAgentCard = (item: ShortlistItem): AgentCard => ({
    id: item.id,
    taskName: shortlistData?.project_name || "",
    taskSummary: shortlistData?.project_summary || "",
    matchPercent: Math.round(item.score * 100),
    agentName: item.metadata.name,
    agentDescription: item.metadata.short_description,
    tags: item.metadata.categories,
  });

  const handleCompare = () => {
    const agents = (shortlistData?.items ?? []).map((item) => ({
      agent_id: item.agent_id,
      name: item.metadata.name,
      short_description: item.metadata.short_description,
      solution: item.metadata.solution,
      provider: item.metadata.provider,
      categories: item.metadata.categories,
      application_area: item.metadata.application_area,
      selected_count: 0,
      score: item.score,
      status: "active" as const,
      main_category: item.metadata.main_category,
    }));
    navigate("/compare-agent", { state: { agents, projectId } });
  };

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold leading-[24px] lg:leading-[28px] tracking-[-0.018em] lg:tracking-[-0.02em] text-[#202020]">
            추천 AI 에이전트
          </h2>
          {agentCount > 0 && (
            <button
              type="button"
              onClick={handleCompare}
              className="flex items-center gap-1 text-base font-bold text-black cursor-pointer "
            >
              에이전트 세부 비교하기
              <img src={IconRight} alt="Arrow Right Icon" className="w-5 h-5" />
            </button>
          )}
        </div>
        <AgentRecommendationBox agentCount={agentCount} />
      </div>

      {hasAgentCards && (
        <div className="flex flex-col gap-4">
          {shortlistData?.items.map((item) => {
            const agent = toAgentCard(item);
            return (
              <AgentCardItem
                key={agent.id}
                agent={agent}
                selected={selectedIds.includes(agent.id)}
                onToggle={() => onToggle(agent.id)}
              />
            );
          })}
        </div>
      )}

      {showOtherAgentCard && (
        <div className=" md:pl-0 flex gap-4 md:gap-6 items-center">
          <div className="w-5 h-5"></div>

          <div className="flex flex-row flex-1 items-center rounded justify-between gap-6 md:gap-8 p-4 md:p-6  bg-[#EBEBEB]">
            <div className="flex flex-col gap-1">
              <h3 className="text-base md:text-xl font-bold tracking-[-0.02em] text-[#202020]">
                다른 에이전트가 필요하신가요?
              </h3>
              <p className="text-base text-[#000000]">
                원하는 에이전트를 찾지 못하셨나요? 당신의 요구에 딱 맞는 맞춤형 AI 에이전트를 요청해
                보세요.
              </p>
            </div>
            <button type="button" className="shrink-0 " onClick={onRequestOtherAgent}>
              <img src={ArrRight} alt="" className="lg:block hidden" />

              <img src={ArrRightMobile} alt="" className="lg:hidden block" />
            </button>
          </div>
        </div>
      )}

      {showEmptyState && (
        <button
          type="button"
          onClick={onRequestOtherAgent}
          className="flex flex-col items-center gap-8 px-6 py-10 rounded-xl border border-[rgba(0,0,0,0.15)] bg-white cursor-pointer hover:bg-black/[0.02] transition-colors"
        >
          <div className="flex items-center justify-center w-20 h-20 rounded-[4px] bg-[#EBEBEB]">
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M20.0003 6.6665C20.9208 6.6665 21.667 7.4127 21.667 8.33317V18.3332H31.667C32.5875 18.3332 33.3337 19.0794 33.3337 19.9998C33.3337 20.9203 32.5875 21.6665 31.667 21.6665H21.667V31.6665C21.667 32.587 20.9208 33.3332 20.0003 33.3332C19.0799 33.3332 18.3337 32.587 18.3337 31.6665V21.6665H8.33366C7.41318 21.6665 6.66699 20.9203 6.66699 19.9998C6.66699 19.0794 7.41318 18.3332 8.33366 18.3332H18.3337V8.33317C18.3337 7.4127 19.0799 6.6665 20.0003 6.6665Z"
                fill="black"
              />
            </svg>
          </div>
          <div className="flex flex-col items-center gap-3">
            <h3 className="text-xl font-bold tracking-[-0.02em] text-[#202020] text-center">
              다른 에이전트가 필요하신가요?
            </h3>
            <p className="text-base text-[#000000] text-center max-w-[257px]">
              원하는 에이전트를 찾지 못하셨나요? 당신의 요구에 딱 맞는 맞춤형 AI 에이전트를 요청해
              보세요.
            </p>
          </div>
        </button>
      )}
    </div>
  );
}
